/**
 * Multi-provider authentication configuration for PING.
 *
 * Defines types and utilities for Auth0 and Authentik provider configurations.
 */

import { env } from '../config/env';

// Supported auth provider types
export type AuthProviderType = 'auth0' | 'authentik';

// Provider configuration interface
export interface AuthProviderConfig {
  type: AuthProviderType;
  enabled: boolean;
  name: string; // Display name
  icon?: string;

  // Auth0 specific
  domain?: string;
  clientId?: string;
  audience?: string;

  // Authentik specific
  url?: string;
  appSlug?: string;
}

// Full auth providers configuration from backend
export interface AuthProvidersConfig {
  auth0: {
    enabled: boolean;
    domain?: string;
    audience?: string;
  };
  authentik: {
    enabled: boolean;
    url?: string;
    client_id?: string;
    app_slug?: string;
  };
  active_providers: string[];
  updated_at?: string;
}

// Provider info for frontend display
export interface ProviderInfo {
  type: AuthProviderType;
  name: string;
  enabled: boolean;

  // Auth0 specific (public)
  domain?: string;
  clientId?: string;
  audience?: string;

  // Authentik specific (public)
  url?: string;
  appSlug?: string;
}

// Enabled providers response from backend
export interface EnabledProvidersResponse {
  providers: Array<{
    type: string;
    name: string;
    domain?: string;
    audience?: string;
    url?: string;
    client_id?: string;
    app_slug?: string;
  }>;
  default_provider?: string;
}

// Storage key for selected provider
const SELECTED_PROVIDER_KEY = 'ping_auth_provider';

/**
 * Get the currently selected auth provider from localStorage.
 */
export function getSelectedProvider(): AuthProviderType | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(SELECTED_PROVIDER_KEY);
  if (stored === 'auth0' || stored === 'authentik') {
    return stored;
  }
  return null;
}

/**
 * Set the selected auth provider in localStorage.
 */
export function setSelectedProvider(provider: AuthProviderType): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(SELECTED_PROVIDER_KEY, provider);
  }
}

/**
 * Clear the selected auth provider from localStorage.
 */
export function clearSelectedProvider(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(SELECTED_PROVIDER_KEY);
  }
}

// Transient flag (sessionStorage): set when a user explicitly picks a provider in the
// selector, so the login screen can auto-start that provider's login instead of making the
// user click "Sign in" again. Consumed (and cleared) once so a cancelled/failed login falls
// back to the manual button rather than looping.
const AUTO_LOGIN_INTENT_KEY = 'ping_auth_autologin';

export function setAutoLoginIntent(): void {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem(AUTO_LOGIN_INTENT_KEY, '1');
  }
}

export function consumeAutoLoginIntent(): boolean {
  if (typeof window === 'undefined') return false;
  const pending = sessionStorage.getItem(AUTO_LOGIN_INTENT_KEY) === '1';
  if (pending) sessionStorage.removeItem(AUTO_LOGIN_INTENT_KEY);
  return pending;
}

/**
 * Get default Auth0 configuration from environment variables.
 */
export function getAuth0EnvConfig(): AuthProviderConfig {
  return {
    type: 'auth0',
    enabled: true,
    name: 'Auth0',
    domain: env.auth0.domain,
    clientId: env.auth0.clientId,
    audience: env.auth0.audience,
  };
}

/**
 * Get Authentik configuration from environment variables.
 */
export function getAuthentikEnvConfig(): AuthProviderConfig | null {
  const url = import.meta.env.VITE_AUTHENTIK_URL;
  const clientId = import.meta.env.VITE_AUTHENTIK_CLIENT_ID;
  const appSlug = import.meta.env.VITE_AUTHENTIK_APP_SLUG;

  if (!url || !clientId) {
    return null;
  }

  return {
    type: 'authentik',
    enabled: import.meta.env.VITE_AUTHENTIK_ENABLED === 'true',
    name: 'Authentik',
    url,
    clientId,
    appSlug,
  };
}

/**
 * Check if Authentik is configured via environment variables.
 */
export function isAuthentikConfigured(): boolean {
  return !!(
    import.meta.env.VITE_AUTHENTIK_URL &&
    import.meta.env.VITE_AUTHENTIK_CLIENT_ID
  );
}

/**
 * Get the default provider when none is selected.
 */
export function getDefaultProvider(): AuthProviderType {
  const defaultFromEnv = import.meta.env.VITE_DEFAULT_AUTH_PROVIDER;
  if (defaultFromEnv === 'authentik' && isAuthentikConfigured()) {
    return 'authentik';
  }
  return 'auth0';
}

/**
 * Build OIDC configuration for Authentik.
 */
export function buildAuthentikOidcConfig(config: AuthProviderConfig) {
  if (config.type !== 'authentik' || !config.url || !config.clientId) {
    throw new Error('Invalid Authentik configuration');
  }

  const baseUrl = config.url.replace(/\/$/, '');
  const appSlug = config.appSlug || 'ping-frontend';

  return {
    authority: `${baseUrl}/application/o/${appSlug}/`,
    client_id: config.clientId,
    redirect_uri: window.location.origin + '/callback',
    post_logout_redirect_uri: window.location.origin,
    scope: 'openid profile email offline_access ping:admin',
    response_type: 'code',
  };
}

/**
 * Standard PING permission scopes.
 */
export const PING_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'read:ping_feedback',
  'write:ping_feedback',
  'delete:ping_feedback',
  'read:ping_projects',
  'write:ping_projects',
  'delete:ping_projects',
  'read:ping_organisations',
  'write:ping_organisations',
  'delete:ping_organisations',
  'admin:ping_system',
  'read:ping_system_monitoring',
  'write:ping_cache',
  'read:ping_analytics',
  'write:ping_analytics',
].join(' ');
